import { Box, Paper, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useTranslate } from 'react-admin'
import { LazyPlot as Plot } from '../common/LazyPlot'
import { formatWeightWithUnit, resolveSeriesUnit, type WeightUnit } from './weightDisplay'

interface WeightEntry {
  weight: string
  // Since ada-core#8 (2026-08-30) the trigger emits only 'kg' | 'lb' and a
  // canonical numeric weight_kg. Display still re-derives the unit from `weight`
  // in-band (see weightDisplay.ts) and does not read these two fields.
  unit: WeightUnit
  weight_kg?: number
  timestamp: number
  recorded_at: string
}

interface WeightHistoryTooltipProps {
  children: React.ReactElement
  weights?: WeightEntry[]
  currentWeight?: string
}

const WeightHistoryTooltip: React.FC<WeightHistoryTooltipProps> = ({
  children,
  weights = [],
  currentWeight = 'N/A',
}) => {
  const translate = useTranslate()

  if (weights.length === 0) {
    return (
      <Tooltip
        title={translate('pos.weight_history.current', { weight: currentWeight })}
        placement="top"
      >
        {children}
      </Tooltip>
    )
  }

  // Sort weights by timestamp (oldest first for proper line chart)
  const sortedWeights = [...weights].sort((a, b) => a.timestamp - b.timestamp)

  // Prepare data for Plotly
  const timestamps = sortedWeights.map((w) => new Date(w.timestamp))
  const weightValues = sortedWeights.map((w) => parseFloat(w.weight))
  // Axis label comes from the SAME in-band parse the rows use, so the chart and
  // the entry list can never assert different units for one pet. Empty when the
  // series is mixed or carries no unit — the points are plotted unconverted, so
  // naming a unit over them would be a claim the data does not support.
  const seriesUnit = resolveSeriesUnit(sortedWeights)

  const tooltipContent = (
    <Paper sx={{ p: 2, maxWidth: 400 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {translate('pos.weight_history.title')}
      </Typography>

      {/* Mini line chart */}
      <Box sx={{ mb: 2 }}>
        <Plot
          data={[
            {
              x: timestamps,
              y: weightValues,
              type: 'scatter',
              mode: 'lines+markers',
              marker: {
                color: '#1976d2',
                size: 6,
                line: { color: '#1976d2', width: 2 },
              },
              line: { color: '#1976d2', width: 3 },
              name: translate('pos.weight_history.series'),
            },
          ]}
          layout={{
            width: 350,
            height: 150,
            margin: { l: 40, r: 20, t: 20, b: 40 },
            xaxis: {
              title: '',
              showgrid: true,
              gridcolor: '#f0f0f0',
              tickformat: '%m/%d',
            },
            yaxis: {
              title: seriesUnit,
              showgrid: true,
              gridcolor: '#f0f0f0',
            },
            plot_bgcolor: 'white',
            paper_bgcolor: 'white',
            font: { size: 10 },
          }}
          config={{
            displayModeBar: false,
            staticPlot: true,
          }}
        />
      </Box>

      {/* Recent entries list */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('pos.weight_history.recent_entries')}
      </Typography>
      <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
        {sortedWeights
          .slice(-5)
          .reverse()
          .map((weight, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2">{formatWeightWithUnit(weight.weight)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(weight.timestamp).toLocaleDateString()}
              </Typography>
            </Box>
          ))}
      </Box>

      {weights.length > 5 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {translate('pos.weight_history.showing_latest', { count: weights.length })}
        </Typography>
      )}
    </Paper>
  )

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            '& .MuiTooltip-arrow': {
              color: 'white',
            },
          },
        },
      }}
    >
      {children}
    </Tooltip>
  )
}

export default WeightHistoryTooltip
